/**
 * Cards across records: the Dashboard's cards in a studio draft ("What each
 * customer owes", board `links-cross-record` 5b.5). A record's own cards stay
 * `StudioRecord.tiles`, judged by compose.
 *
 * A board card is the insight aggregate tree (`InsightAggNode`) with draft
 * keys where the stored tree holds ids, so its last check is the same reader
 * the server and the phone read a stored card through.
 */
import { draftFormulaSpec, draftRollupResult, flattenDraftFormula as flattenDraftFormulaOf, readDraftFormula, type FlatFormula, type SetupDraftField } from '../compose-author';
import { dateConditionValueProblem, isDateConditionOp } from '../date-conditions';
import { formulaFieldsRead, formulaHopsRead, formulaWithFieldIds, type FormulaCalcOp, type FormulaNode, type FormulaResultType } from '../formula';
import {
  INSIGHT_AGG_MAX_DEPTH,
  INSIGHT_AGG_MAX_LEAVES,
  INSIGHT_CARD_SHAPE,
  INSIGHT_WHERE_MAX_VALUES,
  isInsightCardId,
  isListOp,
  readInsightAgg,
  type InsightAgg,
  type InsightAggNode,
  type InsightCardId,
  type InsightRowSource,
  type InsightShape,
  type InsightWhere,
} from '../insights';
import { LIST_MONEY_NOT_TOTALLED } from '../list-money';
import type { StudioBoardCard, StudioCardLeaf, StudioCardNode, StudioCardSource, StudioCardWhere, StudioDraft, StudioRecord } from './draft';
import type { StudioProblem } from './judge';

/** Every leaf of a card's tree, left to right. */
export const boardCardLeaves = (node: StudioCardNode): StudioCardLeaf[] => {
  switch (node.kind) {
    case 'agg':
      return [node];
    case 'const':
      return [];
    case 'calc':
      return [...boardCardLeaves(node.left), ...boardCardLeaves(node.right)];
    case 'round':
      return boardCardLeaves(node.of);
  }
};

const depthOf = (node: StudioCardNode): number => {
  switch (node.kind) {
    case 'agg':
    case 'const':
      return 1;
    case 'calc':
      return 1 + Math.max(depthOf(node.left), depthOf(node.right));
    case 'round':
      return 1 + depthOf(node.of);
  }
};

/** Every record a card's leaves total, once each, in the order they first appear. */
export const boardCardRecords = (card: StudioBoardCard): string[] => [...new Set(boardCardLeaves(card.total).map((leaf) => leaf.record))];

/** The tree as the insight reader reads it: keys in the id slots. */
export const boardCardTree = (node: StudioCardNode): unknown => {
  switch (node.kind) {
    case 'agg':
      return {
        kind: 'agg',
        agg: node.agg,
        recordId: node.record,
        ...(node.of === undefined
          ? {}
          : {
              of:
                node.of.kind === 'measure'
                  ? { kind: 'measure' }
                  : node.of.kind === 'field'
                    ? { kind: 'field', fieldId: node.of.field }
                    : { kind: 'expr', expr: draftFormulaSpec(node.of.formula).expr, onMissing: node.of.formula.onMissing },
            }),
        ...(node.where === undefined
          ? {}
          : {
              filters: node.where.map((one) => ({
                fieldId: one.field,
                op: one.op,
                ...(one.value === undefined ? {} : { value: one.value }),
                ...(one.via === undefined ? {} : { via: one.via }),
              })),
            }),
        ...(node.groupBy === undefined ? {} : { groupFieldId: node.groupBy }),
      };
    case 'const':
      return { kind: 'const', value: node.value };
    case 'calc':
      return { kind: 'calc', op: node.op, left: boardCardTree(node.left), right: boardCardTree(node.right) };
    case 'round':
      return { kind: 'round', of: boardCardTree(node.of), decimals: node.decimals };
  }
};

const NUMBER_TYPES: ReadonlySet<string> = new Set(['money', 'number', 'quantity']);
const NUMBER_RESULTS: ReadonlySet<FormulaResultType> = new Set(['money', 'number', 'quantity']);

const isNumber = (field: SetupDraftField): boolean =>
  NUMBER_TYPES.has(field.type) || (field.type === 'formula' && field.formula !== undefined && NUMBER_RESULTS.has(field.formula.result));

/** Whether a draft field holds an amount: a money field, or a formula or a total that works one out. */
const draftMoney = (field: SetupDraftField, draft: StudioDraft): boolean =>
  field.type === 'money' ||
  (field.type === 'formula' && field.formula?.result === 'money') ||
  (field.type === 'rollup' && draftRollupResult(field, draft.records) === 'money');

/**
 * Whether a list's field is money typed on it, or a formula reading such
 * money: not worked out from records, so no card totals it (W11, narrowed
 * 29 Oct 2026). A total (rollup) never is.
 */
const draftTypedOnList = (field: SetupDraftField, fields: readonly SetupDraftField[], seen: ReadonlySet<string> = new Set()): boolean => {
  if (field.type === 'money') return true;
  if (field.type !== 'formula' || field.formula === undefined || seen.has(field.key)) return false;
  const next = new Set([...seen, field.key]);
  return formulaFieldsRead(draftFormulaSpec(field.formula)).some((key) => {
    const read = fields.find((one) => one.key === key);
    return read !== undefined && draftTypedOnList(read, fields, next);
  });
};

/**
 * Whether a draft record has a time axis: a record with a date. A sum over
 * one is money over a period (billed, received, spent), and a Dashboard card
 * of it takes at most a calendar year (owner, 29 Oct 2026).
 */
const draftTimed = (record: StudioRecord | undefined): boolean =>
  record !== undefined && record.kind === 'record' && (record.dateFieldKey !== undefined || record.fields.some((field) => field.type === 'date' || field.type === 'datetime'));

/** The field a record totals by the author's rule, or undefined when it totals nothing. */
export const studioMeasureOf = (record: StudioRecord): string | undefined => {
  if (record.kind !== 'record' || record.direction === 'none') return undefined;
  const first = (types: readonly string[]): string | undefined => record.fields.find((field) => types.includes(field.type))?.key;
  return record.measureFieldKey ?? first(['money']) ?? (record.direction === 'count' ? first(['quantity', 'number']) : undefined);
};

/** Every problem with one Dashboard card, all at once. Path and part are `board:<key>`. */
export const boardCardProblems = (card: StudioBoardCard, draft: StudioDraft): StudioProblem[] => {
  const path = `board:${card.key}`;
  const problems: StudioProblem[] = [];
  const say = (code: string, message: string): void => {
    problems.push({ code, message, path, part: path });
  };

  const label = card.label.trim();
  if (label.length === 0 || label.length > 80) say('BOARD_LABEL', 'A card is named in 1 to 80 characters.');
  if (!isInsightCardId(card.card)) say('BOARD_CARD_UNKNOWN', `"${String(card.card)}" is not a card the Dashboard draws.`);

  const leaves = boardCardLeaves(card.total);
  if (leaves.length > INSIGHT_AGG_MAX_LEAVES) say('BOARD_TOO_MANY', `A card adds up ${INSIGHT_AGG_MAX_LEAVES} totals at most; this one holds ${leaves.length}.`);
  if (depthOf(card.total) > INSIGHT_AGG_MAX_DEPTH) say('BOARD_TOO_DEEP', `A card's sum nests ${INSIGHT_AGG_MAX_DEPTH} deep at most.`);

  /** The list each leaf groups by, for the breakdown's one-list rule. */
  const groupLists: Array<string | undefined> = [];
  for (const leaf of leaves) {
    const record = draft.records.find((one) => one.key === leaf.record);
    if (record === undefined || (record.kind !== 'record' && record.kind !== 'directory')) {
      say('BOARD_RECORD_UNKNOWN', `"${leaf.record}" is not a record or a list of this draft.`);
      groupLists.push(undefined);
      continue;
    }
    // A list's own rows (W11, narrowed 29 Oct 2026): the money it works out from records, by its name.
    const onList = record.kind === 'directory';
    const fields = new Map(record.fields.map((field) => [field.key, field]));
    /** The fields of the list a reference of this record picks from, or null when it is not one. */
    const listThrough = (via: string): Map<string, SetupDraftField> | null => {
      const hop = fields.get(via);
      if (hop === undefined || hop.type !== 'reference') return null;
      const list = draft.records.find((one) => one.key === hop.targetRecordKey);
      return new Map((list?.fields ?? []).map((field) => [field.key, field]));
    };

    if (leaf.agg !== 'rows' && leaf.of === undefined) {
      say('BOARD_VALUE_MISSING', `A ${leaf.agg} of "${leaf.record}" needs a value to add up.`);
    }
    if (leaf.of?.kind === 'field') {
      const field = fields.get(leaf.of.field);
      if (field === undefined) say('BOARD_FIELD_UNKNOWN', `"${leaf.of.field}" is not a field of "${leaf.record}".`);
      else if (['sum', 'avg', 'min', 'max'].includes(leaf.agg) && onList && draftMoney(field, draft) && draftTypedOnList(field, record.fields)) {
        say(LIST_MONEY_NOT_TOTALLED, `"${field.label}" is money typed on the list, not worked out from records, so no card adds it up.`);
      } else if (['sum', 'avg', 'min', 'max'].includes(leaf.agg) && !isNumber(field) && !(field.type === 'rollup' && draftRollupResult(field, draft.records) !== undefined)) {
        say('BOARD_VALUE_NOT_NUMBER', `A ${leaf.agg} adds up numbers, and "${leaf.of.field}" is not one.`);
      }
    }
    if (leaf.of?.kind === 'measure' && studioMeasureOf(record) === undefined) {
      say('BOARD_NO_MEASURE', `"${leaf.record}" totals nothing, so it has no measure to add up.`);
    }
    if (leaf.of?.kind === 'expr') {
      const spec = draftFormulaSpec(leaf.of.formula);
      for (const key of formulaFieldsRead(spec)) {
        const read = fields.get(key);
        if (read === undefined) say('BOARD_FIELD_UNKNOWN', `The worked-out value reads "${key}", which "${leaf.record}" does not declare.`);
        else if (onList && ['sum', 'avg', 'min', 'max'].includes(leaf.agg) && draftMoney(read, draft) && draftTypedOnList(read, record.fields)) {
          say(LIST_MONEY_NOT_TOTALLED, `"${read.label}" is money typed on the list, not worked out from records, so no card adds it up.`);
        }
      }
      for (const hop of formulaHopsRead(spec)) {
        const list = listThrough(hop.via);
        const there = list?.get(hop.fieldId);
        if (list === null || there === undefined) {
          say('BOARD_FIELD_UNKNOWN', `The worked-out value reads "${hop.fieldId}" through "${hop.via}", and that list does not declare it.`);
        } else if (['sum', 'avg', 'min', 'max'].includes(leaf.agg) && draftMoney(there, draft)) {
          // Cards never total a list's money (W11 of docs/plans/workbook-map.md): it is worked out from records, which the card totals instead.
          say(LIST_MONEY_NOT_TOTALLED, `"${there.label}" is money the list works out from records, so no card adds it up. Total the records it comes from.`);
        }
      }
    }
    for (const where of leaf.where ?? []) {
      let owner: Map<string, SetupDraftField> = fields;
      if (where.via !== undefined) {
        const list = listThrough(where.via);
        if (list === null) {
          say('BOARD_VIA_NOT_REFERENCE', `"${where.via}" on "${leaf.record}" is not a pick from a list, so a condition cannot be asked through it.`);
          continue;
        }
        owner = list;
      }
      if (!owner.has(where.field)) say('BOARD_FIELD_UNKNOWN', `A condition names "${where.field}", which is not there.`);
      const values = where.value === undefined ? [] : Array.isArray(where.value) ? where.value : [where.value];
      const presence = where.op === 'set' || where.op === 'unset';
      const fits = isDateConditionOp(where.op)
        ? !Array.isArray(where.value) && dateConditionValueProblem(where.op, where.value) === null
        : presence
        ? where.value === undefined
        : isListOp(where.op)
          ? Array.isArray(where.value) && values.length >= 1 && values.length <= INSIGHT_WHERE_MAX_VALUES
          : where.value !== undefined && !Array.isArray(where.value);
      if (!fits) say('BOARD_WHERE_VALUES', `The condition on "${where.field}" (${where.op}) does not hold the values that op takes.`);
    }
    if (leaf.groupBy !== undefined && onList) {
      // A list's card lines up on its own rows, by the field that names a row.
      if (leaf.groupBy !== record.headlineFieldKey) {
        say('BOARD_GROUP_NOT_REFERENCE', `A card over a list's rows groups by the field that names a row, and "${leaf.groupBy}" is not it.`);
        groupLists.push(undefined);
      } else {
        groupLists.push(record.key);
      }
    } else if (leaf.groupBy !== undefined) {
      const group = fields.get(leaf.groupBy);
      if (group === undefined || group.type !== 'reference') {
        say('BOARD_GROUP_NOT_REFERENCE', `A card groups by a pick from a list, and "${leaf.groupBy}" is not one.`);
        groupLists.push(undefined);
      } else {
        groupLists.push(group.targetRecordKey);
      }
    } else {
      groupLists.push(undefined);
    }
  }

  const shape = isInsightCardId(card.card) ? INSIGHT_CARD_SHAPE[card.card] : null;
  if (shape === 'breakdown') {
    if (leaves.some((leaf) => leaf.groupBy === undefined)) say('BOARD_GROUP_MISSING', 'A breakdown groups every total by a pick from a list.');
    const lists = new Set(groupLists.filter((list): list is string => list !== undefined));
    if (lists.size > 1) say('BOARD_GROUPS_DIFFER', 'Every total of a breakdown groups by a pick from the same list.');
    if (card.limit !== undefined && (!Number.isInteger(card.limit) || card.limit < 1 || card.limit > 12)) {
      say('BOARD_LIMIT', 'A breakdown lists 1 to 12 rows.');
    }
  } else if (shape !== null && leaves.some((leaf) => leaf.groupBy !== undefined)) {
    say('BOARD_GROUP_NOT_HERE', 'Only a breakdown groups its totals.');
  }
  if (shape === 'series' && (card.days === undefined || !Number.isInteger(card.days) || card.days < 2 || card.days > 92 || card.scope !== undefined)) {
    say('BOARD_DAYS', 'A trend looks back 2 to 92 days, and takes no period.');
  }
  if ((shape === 'scalar' || shape === 'scalar_pair') && card.scope === undefined) {
    say('BOARD_SCOPE_MISSING', 'A single figure needs its period: a day, a week, a month, a year or all time.');
  }

  if (readInsightAgg(boardCardTree(card.total)) === null) say('BOARD_SHAPE', 'The sum is not a shape a card can hold.');
  return problems;
};

/* ------------------------------------------------------------------ the wire */

/** One node of a card's sum as the wire carries it: flat, naming its children by id. */
export type StudioCardNodeWire =
  | {
      id: string;
      kind: 'agg';
      agg: InsightAgg;
      record: string;
      ofKind?: 'measure' | 'field' | 'expr';
      ofField?: string;
      ofFormula?: FlatFormula;
      where?: StudioCardWhere[];
      groupBy?: string;
    }
  | { id: string; kind: 'const'; value: number }
  | { id: string; kind: 'calc'; op: FormulaCalcOp; left: string; right: string }
  | { id: string; kind: 'round'; of: string; decimals: number };

/** A Dashboard card as the wire carries it: its sum a flat list of nodes, `root` the one that answers. */
export type StudioBoardCardWire = Omit<StudioBoardCard, 'key' | 'total'> & { key?: string; root: string; nodes: StudioCardNodeWire[] };

/** The most nodes one card's sum is sent in. */
export const STUDIO_CARD_WIRE_NODES = 24;

/**
 * A card's flat sum read into its tree: ids unique, every child known, no
 * ring, at most `STUDIO_CARD_WIRE_NODES` nodes, no deeper than
 * `INSIGHT_AGG_MAX_DEPTH`. A worked-out value per row (`ofFormula`) is read
 * with the draft's formula reader. Every fault is BOARD_SHAPE, said at once.
 */
export const readBoardCardWire = (
  wire: StudioBoardCardWire,
): { ok: true; card: Omit<StudioBoardCard, 'key'> & { key?: string } } | { ok: false; problems: Array<{ code: string; message: string }> } => {
  const problems: Array<{ code: string; message: string }> = [];
  const shape = (message: string): void => {
    problems.push({ code: 'BOARD_SHAPE', message });
  };
  if (wire.nodes.length === 0 || wire.nodes.length > STUDIO_CARD_WIRE_NODES) shape(`A card's sum is 1 to ${STUDIO_CARD_WIRE_NODES} pieces.`);
  const byId = new Map<string, StudioCardNodeWire>();
  for (const node of wire.nodes) {
    if (byId.has(node.id)) shape(`Two pieces are both called "${node.id}".`);
    byId.set(node.id, node);
  }
  if (!byId.has(wire.root)) shape(`The answer "${wire.root}" is not a piece of the sum.`);

  const used = new Set<string>();
  const build = (id: string, depth: number, path: readonly string[]): StudioCardNode | null => {
    if (path.includes(id)) {
      shape(`The sum goes round in a ring at "${id}".`);
      return null;
    }
    if (depth > INSIGHT_AGG_MAX_DEPTH) {
      shape(`A card's sum nests ${INSIGHT_AGG_MAX_DEPTH} deep at most.`);
      return null;
    }
    const node = byId.get(id);
    if (node === undefined) {
      shape(`"${id}" is not a piece of the sum.`);
      return null;
    }
    used.add(id);
    const here = [...path, id];
    switch (node.kind) {
      case 'const':
        return { kind: 'const', value: node.value };
      case 'calc': {
        const left = build(node.left, depth + 1, here);
        const right = build(node.right, depth + 1, here);
        return left === null || right === null ? null : { kind: 'calc', op: node.op, left, right };
      }
      case 'round': {
        const of = build(node.of, depth + 1, here);
        return of === null ? null : { kind: 'round', of, decimals: node.decimals };
      }
      case 'agg': {
        let of: StudioCardSource | undefined;
        if (node.ofKind === 'measure') of = { kind: 'measure' };
        else if (node.ofKind === 'field') {
          if (node.ofField === undefined) {
            shape(`"${id}" adds up a field and names none.`);
            return null;
          }
          of = { kind: 'field', field: node.ofField };
        } else if (node.ofKind === 'expr') {
          if (node.ofFormula === undefined) {
            shape(`"${id}" adds up a worked-out value and gives none.`);
            return null;
          }
          const read = readDraftFormula(node.ofFormula, `board.${id}`);
          if (!read.ok) {
            for (const problem of read.problems) shape(`"${id}": ${problem.message}`);
            return null;
          }
          of = { kind: 'expr', formula: read.value };
        }
        return {
          kind: 'agg',
          agg: node.agg,
          record: node.record,
          ...(of === undefined ? {} : { of }),
          ...(node.where === undefined ? {} : { where: node.where }),
          ...(node.groupBy === undefined ? {} : { groupBy: node.groupBy }),
        };
      }
    }
  };
  const total = problems.length === 0 ? build(wire.root, 1, []) : null;
  if (total !== null && used.size !== byId.size) shape('Every piece of the sum must be reached from the answer.');
  if (total === null || problems.length > 0) return { ok: false, problems: problems.length > 0 ? problems : [{ code: 'BOARD_SHAPE', message: 'The sum is not a shape a card can hold.' }] };
  const { root: _root, nodes: _nodes, ...card } = wire;
  return { ok: true, card: { ...card, total } };
};

/** A card's tree as the flat wire, for the scripted model and the round-trip test. */
export const flattenBoardCardTotal = (total: StudioCardNode): { root: string; nodes: StudioCardNodeWire[] } => {
  const nodes: StudioCardNodeWire[] = [];
  let counter = 0;
  const add = (node: StudioCardNode): string => {
    const id = `c${(counter += 1)}`;
    switch (node.kind) {
      case 'const':
        nodes.push({ id, kind: 'const', value: node.value });
        return id;
      case 'calc': {
        const left = add(node.left);
        const right = add(node.right);
        nodes.push({ id, kind: 'calc', op: node.op, left, right });
        return id;
      }
      case 'round': {
        const of = add(node.of);
        nodes.push({ id, kind: 'round', of, decimals: node.decimals });
        return id;
      }
      case 'agg':
        nodes.push({
          id,
          kind: 'agg',
          agg: node.agg,
          record: node.record,
          ...(node.of === undefined
            ? {}
            : node.of.kind === 'measure'
              ? { ofKind: 'measure' as const }
              : node.of.kind === 'field'
                ? { ofKind: 'field' as const, ofField: node.of.field }
                : { ofKind: 'expr' as const, ofFormula: flattenDraftFormulaOf(node.of.formula) }),
          ...(node.where === undefined ? {} : { where: node.where }),
          ...(node.groupBy === undefined ? {} : { groupBy: node.groupBy }),
        });
        return id;
    }
  };
  const root = add(total);
  return { root, nodes };
};

/**
 * A card's tree as one string whatever order its keys were written in, so two
 * cards that say the same thing compare equal: one drawn by OS and read back
 * from the store, one drawn from the map in code. Never by the label.
 */
export const cardTreeKey = (node: unknown): string => {
  const sorted = (one: unknown): unknown =>
    Array.isArray(one)
      ? one.map(sorted)
      : one !== null && typeof one === 'object'
        ? Object.fromEntries(Object.keys(one).sort().map((key) => [key, sorted((one as Record<string, unknown>)[key])]))
        : one;
  return JSON.stringify(sorted(node) ?? null);
};

/* ------------------------------------------------------- the cards Build keeps */

/** The ids Build made, by the draft's keys: each record, each record's fields, each field's choices. */
export type BuiltBoardIds = {
  readonly records: Readonly<Record<string, string>>;
  readonly fields: Readonly<Record<string, Readonly<Record<string, string>>>>;
  readonly options: Readonly<Record<string, Readonly<Record<string, Readonly<Record<string, string>>>>>>;
};

/**
 * A Dashboard card as the device reads one hosted on its Dashboard: the
 * record spec of `host` (`InsightSpec` in apps/mobile's insight-query.ts),
 * its sum the insight aggregate tree by ids. `host` is the record of the
 * card's first total, which owns the group field a breakdown lines up on.
 */
export type WorkspaceBoardSpec = {
  shape: InsightShape;
  measure: { agg: 'formula'; expr: InsightAggNode };
  scope?: StudioBoardCard['scope'];
  days?: number;
  groupFieldId?: string;
  limit?: number;
};

/** A setup draft's Dashboard card, kept for the workspace at Build (`workspaceBoardCards`). */
export type WorkspaceBoardCard = {
  label: string;
  card: InsightCardId;
  host: string;
  spec: WorkspaceBoardSpec;
};

/** The rows a breakdown draws when its card names none: the device's own default for a record's breakdown. */
const BREAKDOWN_ROWS = 4;

/** Thrown inside a card's translation when it names a part Build did not make; the card is then not kept. */
class NotBuilt extends Error {}

const builtOf = <T>(value: T | undefined): T => {
  if (value === undefined) throw new NotBuilt();
  return value;
};

/** A worked-out value per row, its reads by id: a hop's far field by its list's ids, then every field and pick of the record. */
const builtFormula = (spec: ReturnType<typeof draftFormulaSpec>, record: StudioRecord, ids: BuiltBoardIds): InsightRowSource => {
  const own = ids.fields[record.key] ?? {};
  const far = (node: FormulaNode): FormulaNode => {
    switch (node.kind) {
      case 'hop': {
        const via = record.fields.find((field) => field.key === node.via);
        const list = via?.type === 'reference' ? via.targetRecordKey : undefined;
        return { ...node, fieldId: builtOf(list === undefined ? undefined : ids.fields[list]?.[node.fieldId]) };
      }
      case 'field':
        builtOf(own[node.fieldId]);
        return node;
      case 'const':
      case 'today':
        return node;
      case 'calc':
        return { ...node, left: far(node.left), right: far(node.right) };
      case 'round':
        return { ...node, of: far(node.of) };
      case 'if':
        return { ...node, left: far(node.left), ...(node.right === undefined ? {} : { right: far(node.right) }), then: far(node.then), otherwise: far(node.otherwise) };
      case 'days_between':
        return { ...node, from: far(node.from), to: far(node.to) };
      case 'concat':
        return { ...node, parts: node.parts.map(far) };
      case 'coalesce':
        return { ...node, of: node.of.map(far) };
      case 'date_add':
        return { ...node, date: far(node.date), amount: far(node.amount), unit: far(node.unit) };
      // A total over a bill's lines reads a field of the lines, which Build names apart: not a card's value yet.
      case 'agg':
        throw new NotBuilt();
    }
  };
  const read = formulaWithFieldIds({ ...spec, expr: far(spec.expr) }, new Map(Object.entries(own)));
  return { kind: 'expr', expr: read.expr, onMissing: read.onMissing };
};

/** One condition by ids: its field on the record or, with `via`, on the list the pick reads; a choice by the id Build minted. */
const builtWhere = (where: StudioCardWhere, record: StudioRecord, draft: StudioDraft, ids: BuiltBoardIds): InsightWhere => {
  let ownerKey = record.key;
  if (where.via !== undefined) {
    const via = record.fields.find((field) => field.key === where.via);
    ownerKey = builtOf(via?.type === 'reference' ? via.targetRecordKey : undefined);
  }
  const owner = draft.records.find((one) => one.key === ownerKey);
  const field = owner?.fields.find((one) => one.key === where.field);
  const choices = field?.type === 'select' ? ids.options[ownerKey]?.[where.field] ?? {} : null;
  const choice = (value: string | number): string | number => (choices === null || typeof value !== 'string' ? value : builtOf(choices[value]));
  return {
    fieldId: builtOf(ids.fields[ownerKey]?.[where.field]),
    op: where.op,
    ...(where.value === undefined ? {} : { value: Array.isArray(where.value) ? where.value.map(choice) : choice(where.value as string | number) }),
    ...(where.via === undefined ? {} : { via: builtOf(ids.fields[record.key]?.[where.via]) }),
  };
};

const builtTree = (node: StudioCardNode, draft: StudioDraft, ids: BuiltBoardIds): InsightAggNode => {
  switch (node.kind) {
    case 'const':
      return { kind: 'const', value: node.value };
    case 'calc':
      return { kind: 'calc', op: node.op, left: builtTree(node.left, draft, ids), right: builtTree(node.right, draft, ids) };
    case 'round':
      return { kind: 'round', of: builtTree(node.of, draft, ids), decimals: node.decimals };
    case 'agg': {
      const record = builtOf(draft.records.find((one) => one.key === node.record));
      const fields = ids.fields[record.key] ?? {};
      const of: InsightRowSource | undefined =
        node.of === undefined
          ? undefined
          : node.of.kind === 'measure'
            ? { kind: 'measure' }
            : node.of.kind === 'field'
              ? { kind: 'field', fieldId: builtOf(fields[node.of.field]) }
              : builtFormula(draftFormulaSpec(node.of.formula), record, ids);
      return {
        kind: 'agg',
        agg: node.agg,
        recordId: builtOf(ids.records[record.key]),
        ...(of === undefined ? {} : { of }),
        ...(node.where === undefined ? {} : { filters: node.where.map((where) => builtWhere(where, record, draft, ids)) }),
        ...(node.groupBy === undefined ? {} : { groupFieldId: builtOf(fields[node.groupBy]) }),
      };
    }
  }
};

/**
 * Whether a card sums money over a period: a sum (or an average, a least, a
 * most) over a record with a time axis, as billed, received, spent and
 * profit are. A count of rows and a list's stored balance are not. Such a
 * card takes at most a calendar year (owner, 29 Oct 2026).
 */
export const flowCard = (card: Pick<StudioBoardCard, 'total'>, draft: StudioDraft): boolean =>
  boardCardLeaves(card.total).some((leaf) => leaf.agg !== 'rows' && leaf.agg !== 'count' && draftTimed(draft.records.find((one) => one.key === leaf.record)));

/**
 * The draft's Dashboard cards by the ids Build made, kept for the workspace
 * so a device with no Dashboard of its own starts from them. A sum over a
 * record with a time axis that said all time takes this year (`flowCard`).
 * A card with any problem is not kept: among them a total of money typed on
 * a list or read through a pick (W11, `LIST_MONEY_NOT_TOTALLED`). Nor is a card that names a part,
 * a field or a choice Build did not make, or one the insight reader cannot
 * read back. Nor is a second card of the same sum, shape, grouping and
 * period as one kept before it, whatever its label: the Dashboard says one
 * figure once.
 */
export const workspaceBoardCards = (draft: StudioDraft, ids: BuiltBoardIds): WorkspaceBoardCard[] => {
  const seen = new Set<string>();
  return draft.boardCards.flatMap((card): WorkspaceBoardCard[] => {
    if (boardCardProblems(card, draft).length > 0) return [];
    let expr: InsightAggNode;
    try {
      expr = builtTree(card.total, draft, ids);
    } catch (error) {
      if (error instanceof NotBuilt) return [];
      throw error;
    }
    if (readInsightAgg(expr) === null) return [];
    const first = boardCardLeaves(card.total)[0];
    if (first === undefined) return [];
    const host = ids.records[first.record]!;
    const shape = INSIGHT_CARD_SHAPE[card.card];
    const group = first.groupBy === undefined ? undefined : ids.fields[first.record]?.[first.groupBy];
    const scope = card.scope === 'all' && flowCard(card, draft) ? 'year' : card.scope;
    const spec: WorkspaceBoardSpec = {
      shape,
      measure: { agg: 'formula', expr },
      ...(shape === 'breakdown' && group !== undefined ? { groupFieldId: group } : {}),
      ...(shape === 'series' ? { days: card.days } : scope === undefined ? {} : { scope }),
      ...(shape === 'breakdown' ? { limit: card.limit ?? BREAKDOWN_ROWS } : {}),
    };
    const same = cardTreeKey({ shape, measure: spec.measure, groupFieldId: spec.groupFieldId, scope: spec.scope, days: spec.days });
    if (seen.has(same)) return [];
    seen.add(same);
    return [{ label: card.label.trim(), card: card.card, host, spec }];
  });
};

/** The leaves of a kept card's sum, left to right. */
export const aggLeavesOf = (node: InsightAggNode): Extract<InsightAggNode, { kind: 'agg' }>[] => {
  switch (node.kind) {
    case 'agg':
      return [node];
    case 'const':
      return [];
    case 'calc':
      return [...aggLeavesOf(node.left), ...aggLeavesOf(node.right)];
    case 'round':
      return aggLeavesOf(node.of);
  }
};

/** Every record a kept card's totals read, once each: its host's own when a total names none. */
export const boardCardRecordIds = (card: WorkspaceBoardCard): string[] => [...new Set(aggLeavesOf(card.spec.measure.expr).map((leaf) => leaf.recordId ?? card.host))];
